import { LabelSource, type Asset } from '@/api/generatedApi';
import { Ellipsis } from '@/components/Ellipsis';
import { RoundedSquareIconButton } from '@/components/RoundedSquareIconButton';
import { LabelAssetModal } from '@/components/modals/LabelAssetModal';
import { useGetTenantTaxonomy } from '@/hooks/api/taxonomy/useGetTenantTaxonomy';
import { useUnassignLabel } from '@/hooks/api/taxonomy/useUnassignLabel';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { getDepthColor } from '@/pages/TaxonomyPage/TaxonomyTree/taxonomyTreeConstants';
import { isLabelPending } from '@/utils/assets';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Close, Edit, Label } from '@mui/icons-material';
import { Box, Button, Chip, Popover, Skeleton, Tooltip, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { AssetTableCellLabel, AssetTableCellValue, AssetTableRow } from '../AssetDetails/AssetTable';

export const RemoveConfirmation = ({
  anchorEl,
  onConfirm,
  onCancel,
}: {
  anchorEl: HTMLElement | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onCancel}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    slotProps={{ paper: { sx: { borderRadius: '12px', mt: 0.5, p: 1.5 } } }}
  >
    <Typography variant='body2' sx={{ mb: 1 }}>
      Remove this label?
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
      <Button size='small' onClick={onCancel}>
        Cancel
      </Button>
      <Button size='small' variant='contained' color='error' onClick={onConfirm}>
        Remove
      </Button>
    </Box>
  </Popover>
);

const LabelChip = ({
  name,
  depth,
  source,
  onRemove,
  isRemoving,
}: {
  name: string;
  depth: number;
  source?: LabelSource;
  onRemove: () => void;
  isRemoving: boolean;
}) => {
  const color = getDepthColor(depth);
  const [isTruncated, setIsTruncated] = useState(false);
  const [confirmAnchor, setConfirmAnchor] = useState<HTMLElement | undefined>();
  const labelReference = useRef<HTMLSpanElement>(null);

  const checkTruncation = () => {
    if (labelReference.current) {
      setIsTruncated(labelReference.current.scrollWidth > labelReference.current.clientWidth);
    }
  };

  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setConfirmAnchor(event.currentTarget);
  }, []);

  const showAiBadge = source === LabelSource.PhysnaGenerated;

  return (
    <>
      <Tooltip title={isTruncated ? name : ''} arrow>
        <Chip
          icon={<Label sx={{ fontSize: 14, color: `${color} !important` }} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
              <Typography
                ref={labelReference}
                variant='body2'
                onMouseEnter={checkTruncation}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.8125rem',
                  lineHeight: 'inherit',
                }}
              >
                {name}
              </Typography>
              {showAiBadge && (
                <Tooltip title='Auto-generated by Physna AI' arrow>
                  <AutoAwesomeIcon sx={{ fontSize: 12, color: 'secondary.main', flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>
          }
          size='small'
          variant='outlined'
          onDelete={handleDeleteClick}
          deleteIcon={<Close sx={{ fontSize: 12 }} />}
          disabled={isRemoving}
          sx={{
            borderColor: color,
            color: 'text.primary',
            '& .MuiChip-label': { px: 0.75, overflow: 'hidden' },
            height: 24,
            maxWidth: '100%',
          }}
        />
      </Tooltip>
      <RemoveConfirmation
        anchorEl={confirmAnchor}
        onConfirm={() => {
          setConfirmAnchor(undefined);
          onRemove();
        }}
        onCancel={() => setConfirmAnchor(undefined)}
      />
    </>
  );
};

export const AssetLabelRow = ({ asset }: { asset: Asset }) => {
  const { isTaxonomyEnabled } = useFeatureFlags();
  const { taxonomy } = useGetTenantTaxonomy();
  const { unassignLabel, isUnassigning } = useUnassignLabel(asset.id);
  const [modalOpen, setModalOpen] = useState(false);

  if (!isTaxonomyEnabled || !taxonomy) {
    return;
  }

  const summary = asset.label?.summary;
  const showLabelPending = !summary && isLabelPending(asset);

  return (
    <>
      <AssetTableRow sx={{ height: 36 }}>
        <AssetTableCellLabel>
          <Ellipsis text='Label' />
        </AssetTableCellLabel>
        <AssetTableCellValue sx={{ py: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              {summary && (
                <LabelChip
                  name={summary.name}
                  depth={summary.depth}
                  source={summary.source}
                  onRemove={() => unassignLabel(taxonomy.id)}
                  isRemoving={isUnassigning}
                />
              )}
              {showLabelPending && (
                <Tooltip title='Auto-labeling in progress…'>
                  <Skeleton variant='rounded' width={96} height={20} sx={{ borderRadius: '12px' }} />
                </Tooltip>
              )}
              {!summary && !showLabelPending && <Ellipsis text='—' />}
            </Box>
            <Box className='action-buttons' sx={{ display: 'flex', gap: '4px' }}>
              <Tooltip title={summary ? 'Change label' : 'Assign a label'}>
                <RoundedSquareIconButton
                  sx={{ backgroundColor: 'background.paper' }}
                  size='small'
                  onClick={() => setModalOpen(true)}
                >
                  <Edit sx={{ fontSize: '12px' }} />
                </RoundedSquareIconButton>
              </Tooltip>
            </Box>
          </Box>
        </AssetTableCellValue>
      </AssetTableRow>

      <LabelAssetModal asset={asset} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};
